/* Global imports. */
import React, { useEffect, useState } from "react";
import { useSiteMetadata } from "@src/hooks/useSiteMetaData";

/* Other imports. */
import { ResolveFormat, ResolveFormatSchema } from "@src/helpers";
import { PanMacmillanEdition } from "@src/models/panmacmillan";
import ImageKit from "../ImageKit";
import { Script } from "gatsby";

interface Props {
  siteRoot?: string;
  title: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  lang?: string;
  contentType: string;
  url: string;
  canonicalURL?: string;
  noIndex?: boolean;
  paginationPrev?: string;
  paginationNext?: string;
  openGraphTitle?: string;
  openGraphDescription?: string;
  twitterCardTitle?: string;
  twitterCardDescription?: string;
  displayDate?: string;
  modifiedDate?: Date;
  bookDetails?: PanMacmillanEdition;
  alternateEditions?: Array<PanMacmillanEdition>;
  eventDetails?: any;
  children?: any;
}

const SEO = ({
  title,
  description = "",
  image = "",
  imageAlt = "",
  contentType,
  url,
  canonicalURL = "",
  noIndex = false,
  paginationPrev,
  paginationNext,
  openGraphTitle,
  openGraphDescription,
  twitterCardTitle,
  twitterCardDescription,
  displayDate,
  modifiedDate,
  bookDetails,
  alternateEditions,
  eventDetails,
  children,
}: Props) => {
  const {
    title: defaultTitle,
    description: defaultDescription,
    siteRoot,
    author: siteAuthor,
    useBookBuy,
    useBookBuyStaging,
    bookBuyApiKey
  } = useSiteMetadata();
  const hasCanonical = contentType === "home" || canonicalURL || url ? true : false;
  const metaDescription = description || defaultDescription;

  const editionsJsonLD =
    alternateEditions &&
    alternateEditions.map((edition) => {
      return {
        "@type": "Book",
        isbn: edition.isbn,
        bookEdition: ResolveFormat(edition.format),
        bookFormat: ResolveFormatSchema(edition.format),
      };
    });
	
	function getStructuredBreadcrumbs() {
    const breadcrumbs = new Array();
	breadcrumbs.push({
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: `${siteRoot}`,
              });
			  
	breadcrumbs.push({
                "@type": "ListItem",
                position: 2,
                name: "Authors & Illustrators",
                item: `${siteRoot}/authors`,
              });
			  
	let breadcrumb;
	
	if(bookDetails.contributors &&
                  bookDetails.contributors[0] &&
				  bookDetails.contributors[0].contributorDetails &&
                  bookDetails.contributors[0].contributorDetails[0] &&
                  bookDetails.contributors[0].contributorDetails[0].personName &&
				  bookDetails.contributors[0].contributorDetails[0].contributorID
				  )
	{
		breadcrumb = {
			"@type": "ListItem",
			position: breadcrumbs.length + 1,
			name: bookDetails.contributors[0].contributorDetails[0].personName,
			image: ImageKit({image: `tr:di-placeholder_portrait_aMjPtD9YZ.jpg,tr:w-350,f-jpg,pr-true/contributor/${bookDetails.contributors[0].contributorDetails[0].contributorID}.jpg`})
		  };
		  
		 if(bookDetails.contributors[0].contributorDetails[0].slug)
		 {
			breadcrumb.item = `${siteRoot}/${bookDetails.contributors[0].contributorDetails[0].slug}`
		 }
		breadcrumbs.push(breadcrumb);
	}
	
	if(bookDetails.titleFull)
	{
		breadcrumb = {
			"@type": "ListItem",
			position: breadcrumbs.length + 1,
			name: bookDetails.titleFull,
			image: ImageKit({image: `tr:di-placeholder_portrait_aMjPtD9YZ.jpg,tr:w-350,f-jpg,pr-true/edition/${bookDetails.isbn}.jpg`})
		  };
		  
		 if(bookDetails.fields && bookDetails.fields.slug)
		 {
			breadcrumb.item = `${siteRoot}/${bookDetails.fields.slug}`
		 }
		breadcrumbs.push(breadcrumb);
	}
    return breadcrumbs;
  }

  function ensureDateisISOFormat(existingDate: any) {
    const d = new Date(existingDate);
    return d.toISOString();
  }


  return (
    <>
      <title>{`${title} - ${defaultTitle}`}</title>
	  <link rel="preload" as="font" href="/fonts/9cf0b086-1d0f-4fca-8d69-91f887fccded.woff2" type="font/woff2" crossOrigin="" />
	  <link rel="preload" as="font" href="/fonts/27c46a82-5633-4ad2-b344-5a8a72cbe74f.woff2" type="font/woff2" crossOrigin="" />
      <meta name="description" content={metaDescription} />
      <meta property="og:url" content={`${siteRoot}/${canonicalURL || url}`} />
      <meta property="og:title" content={openGraphTitle || title} />
      <meta property="og:description" content={openGraphDescription || description} />
      <meta property="og:type" content="website" />
      {image && image !== "" && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@panmacmillan" />
      <meta name="twitter:creator" content={siteAuthor} />
      <meta name="twitter:title" content={twitterCardTitle || title} />
      <meta name="twitter:description" content={twitterCardDescription || description} />
      {image && image !== "" && <meta name="twitter:image" content={image} />}
      {image && image !== "" && <meta name="twitter:image:alt" content={imageAlt || title} />}

      {useBookBuy && (
        <script key="book-buy" type="module" data-ak={bookBuyApiKey} data-at="1" src={useBookBuyStaging ? `https://bookbuy.lineindustries.com/site/blgs/templates/js/buylink.js` : `https://bookbuy.io/site/blgs/templates/js/buylink.js`}></script>
    )}
	  
      {contentType === "article" && <meta name="robots" content="max-image-preview:large" />}
      {contentType === "article" && (
        <script key="article-structured-data" type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "NewsArticle",
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${siteRoot}/${canonicalURL ? canonicalURL : url}`,
            },
            headline: title,
            image: {
              "@type": "ImageObject",
              url: image,
              height: "740",
              width: "416",
            },
            datePublished: displayDate && ensureDateisISOFormat(displayDate),
            dateModified: modifiedDate && ensureDateisISOFormat(modifiedDate),
            author: {
              "@type": "Person",
              name: "Pan Macmillan",
            },
            description: description,
            publisher: {
              "@type": "Organization",
              name: "Pan Macmillan",
              logo: {
                "@type": "ImageObject",
                url: ImageKit({image: "general/PanMacLogoWhite.png"}),
                width: 160,
                height: 23,
              },
            },
          })}
        </script>
      )}

      {paginationPrev && <link rel="prev" href={`${siteRoot}/${paginationPrev}`} />}
      {paginationNext && <link rel="next" href={`${siteRoot}/${paginationNext}`} />}

      {hasCanonical && (
        <link
          rel="canonical"
          href={
            contentType === "home" ? siteRoot : `${siteRoot}/${canonicalURL ? canonicalURL : url}`
          }
        />
      )}

      {noIndex && <meta name="robots" content="noindex" />}

      {contentType === "event" && eventDetails && (
        <script key="event-structured-data" type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "Event",
            name: eventDetails.title,
            url: eventDetails.url,
            startDate: ensureDateisISOFormat(eventDetails.startDateTime),
            endDate: ensureDateisISOFormat(eventDetails.endDateTime),
            image: eventDetails.image.url,
            description: eventDetails.shortDescription,
            location: eventDetails.isOnline
              ? `Online`
              : `${eventDetails.location}, ${eventDetails.address}`,
            price: `${eventDetails.currency} ${eventDetails.price}`,
            organizer: eventDetails.organiser,
          })}
        </script>
      )}

      

      {contentType === "edition" && bookDetails && (
		<>
		<script key="edition-structured-data" type="application/ld+json">
{JSON.stringify({
            "@context": "http://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: getStructuredBreadcrumbs(),
          })}
		</script>
		{ bookDetails.contributors &&
                bookDetails.contributors[0] &&
                bookDetails.contributors[0].contributorDetails &&
                bookDetails.contributors[0].contributorDetails[0] &&
                bookDetails.contributors[0].contributorDetails[0].personName && (
        <script key="book-structured-data" type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "Book",
            name: bookDetails.titleFull,
            url: `${siteRoot}/${bookDetails.fields.slug}`,
            workExample: editionsJsonLD,
            author: {
              "@type": "Person",
              name:bookDetails.contributors[0].contributorDetails[0].personName,
            },
          })}
        </script>)}
		</>
      )}

      {children}
    </>
  );
};

export default SEO;
